/**
 * Phase 17 — the gateway verdict ↔ draft, on the verdict satellite: "the
 * gateway is terrible when …" (a flat ALL/ANY rule; an advanced tree is
 * preserved by OMISSION on save) and the settled SMS a route needs today.
 * An empty rule saves as null — gateways are then never judged.
 * For Media Devoted | Created By Ciro Autiero
 */
import { DROPS_SOURCE, type DropGenVerdictDetail, parseVerdictSources } from '@trafficpulse/shared';

import type { DraftGatewayVerdict } from '../../../../drop-generator.types';
import { flatRuleToTree, parseFlatRule } from './verdict-draft';

export const DEFAULT_GATEWAY_VERDICT: DraftGatewayVerdict = {
  enabled: false,
  rule: { join: 'AND', rows: [], advanced: false },
  minSampleSent: 0,
  timesN: 1,
  timesMode: 'TOTAL',
  source: DROPS_SOURCE,
  blockScope: 'CONTAINER',
};

/** The dead-route starter: SMS went out, nobody arrived at all. */
export const DEAD_ROUTE_ROWS = [
  { metric: 'visits', op: '=', value: 0 },
  { metric: 'smsClicks', op: '=', value: 0 },
];

export function gatewayVerdictToDraft(v: DropGenVerdictDetail | null): DraftGatewayVerdict {
  if (!v) return { ...DEFAULT_GATEWAY_VERDICT };
  return {
    enabled: v.gatewayEnabled ?? v.gatewayVerdictJson != null,
    rule: parseFlatRule(v.gatewayVerdictJson ?? null),
    minSampleSent: v.gatewayMinSampleSent ?? DEFAULT_GATEWAY_VERDICT.minSampleSent,
    timesN: v.gatewayTimesN ?? 1,
    timesMode: v.gatewayTimesMode === 'ROW' ? 'ROW' : 'TOTAL',
    source: parseVerdictSources(v.sourcesJson).gatewayVerdict,
    blockScope: v.gatewayBlockScope === 'COUNTRY' ? 'COUNTRY' : 'CONTAINER',
  };
}

/** The verdict PUT's gateway fields; an advanced tree is simply not sent. */
export function gatewayVerdictToBody(d: DraftGatewayVerdict): Record<string, unknown> {
  return {
    gatewayEnabled: d.enabled,
    gatewayMinSampleSent: d.minSampleSent,
    gatewayTimesN: d.timesN,
    gatewayTimesMode: d.timesMode,
    gatewayBlockScope: d.blockScope,
    ...(d.rule.advanced ? {} : { gatewayVerdictJson: flatRuleToTree(d.rule) }),
  };
}
